import { useUserContext } from "@contexts/UserContext/index.context";
import EditIcon from "@mui/icons-material/Edit";
import {
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Container from "@mui/material/Container";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type {
  DeliveryAddressFieldsFragment,
  DeliveryOptionSelectionFieldsFragment,
  SummaryAvailabilityFieldsFragment,
  SummaryDriverFieldsFragment,
  SummaryPartyFieldsFragment,
  VehicleSectionFieldsFragment,
} from "@/libs/generated/operations";
import { formatTimeDate } from "@/utils/date";
import { AddOptionModal } from "./add-option-modal";
import AdditionalOptionsDisplay from "./additional-options-display";

type SummaryDataShape = DeliveryAddressFieldsFragment &
  SummaryPartyFieldsFragment &
  SummaryAvailabilityFieldsFragment &
  SummaryDriverFieldsFragment & {
    id?: number;
    category?: string | null;
    comment?: string | null;
    vehicles?: Array<VehicleSectionFieldsFragment> | null;
    selectedOptions?: DeliveryOptionSelectionFieldsFragment[] | null;
    organization?: { slug?: string } | null;
  };

const categories: Record<string, string> = {
  STANDARD: "STANDARD",
  EXPRESS: "EXPRESS (Moins 48H)",
  OKM: "OKM",
};

type SummaryProps = {
  isRestitution: boolean;
  data: SummaryDataShape;
  isCreation?: boolean;
  editable?: boolean;
};

type DataItem = {
  key: string;
  value: React.ReactNode;
};

type TDataComponent = {
  title: string;
  data: DataItem[];
};

function DataComponent({ title, data }: TDataComponent) {
  const { t } = useTranslation();
  return (
    <Paper sx={{ backgroundColor: "transparent", padding: 3, height: "100%" }}>
      <Typography color="#ff5c8d" textTransform="capitalize" variant="h5">
        {title}
      </Typography>
      <Stack direction="column" marginTop={2} spacing={1}>
        {data.map((item, index) => {
          if (!item.key) {
            return <div key={`${title}_divider_${index}`}>{item.value}</div>;
          }

          return (
            <Typography key={`${title}_${item.key}_${index}`} variant="body1" component="div">
              <Typography
                color="text.primary"
                component="span"
                fontWeight={600}
                sx={{ letterSpacing: "0.03em" }}
                textTransform="capitalize"
              >
                {item.key}
              </Typography>{" "}
              {item.value !== null && item.value !== undefined && item.value !== "" ? (
                item.value
              ) : (
                <span style={{ textTransform: "capitalize", color: "gray" }}>
                  {t("delivery.summary.not-specified")}
                </span>
              )}
            </Typography>
          );
        })}
      </Stack>
    </Paper>
  );
}

export default function Summary({ data, isRestitution, editable = true }: SummaryProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userState } = useUserContext();
  if (!(data && userState)) {
    throw new Error("Data or userState is missing");
  }
  const isAdmin = userState["custom:role"] === "ADMIN";
  const mondataire = {
    firstName: data.representativeFirstName ?? "",
    lastName: data.representativeLastName ?? "",
    phoneNumber: data.representativePhone ?? "",
    email: data.representativeEmail ?? "",
  };
  const [addOptionOpen, setAddOptionOpen] = useState(false);
  const selectedOptions = data.selectedOptions ?? [];
  const vehicles = data.vehicles ?? [];
  const alreadyLinkedIds = new Set(selectedOptions.map((o) => o.optionId));
  const organizationSlug = data.organization?.slug ?? "";
  return (
    <Container className="summary" component="main" maxWidth="xl">
      <Grid container spacing={2}>
        <Grid size={{ xl: 6, xs: 12 }}>
          <DataComponent
            data={[
              {
                key: t("delivery.summary.departure"),
                value: data.fromAddress?.address,
              },
              {
                key: `${t("delivery.summary.precision-address")} (${t("delivery.summary.departure")})`,
                value: data.fromAddress?.addressPrecision,
              },
              {
                key: "",
                value: <Divider sx={{ my: 1 }} />,
              },
              {
                key: t("delivery.summary.arrival"),
                value: data.toAddress?.address,
              },
              {
                key: `${t("delivery.summary.precision-address")} (${t("delivery.summary.arrival")})`,
                value: data.toAddress?.addressPrecision,
              },
            ]}
            title={t("delivery.summary.delivery-address")}
          />
        </Grid>

        {isRestitution && (
          <Grid size={{ xl: 6, xs: 12 }}>
            <DataComponent
              data={[
                {
                  key: t("delivery.summary.departure"),
                  value: data.toReturnFromAddress?.address,
                },
                {
                  key: t("delivery.summary.arrival"),
                  value: data.toReturnToAddress?.address,
                },
              ]}
              title={t("delivery.summary.restitution-address")}
            />
          </Grid>
        )}
        <Grid size={{ xl: 6, xs: 12 }}>
          <DataComponent
            data={[
              {
                key: t("delivery.summary.name"),
                value: `${mondataire.firstName} ${mondataire.lastName}`,
              },
              {
                key: t("common.form.input.phone"),
                value: mondataire.phoneNumber,
              },
              { key: t("common.form.input.email"), value: mondataire.email },
            ]}
            title={t("delivery.summary.agent-information")}
          />
        </Grid>
        <Grid size={{ xl: 6, xs: 12 }}>
          <DataComponent
            data={[
              {
                key: t("delivery.summary.name"),
                value: `${data?.clientFirstName} ${data?.clientLastName}`,
              },
              { key: t("common.form.input.phone"), value: data?.clientPhone },
              { key: t("common.form.input.email"), value: data?.clientEmail },
              { key: t("client.form.siret"), value: data?.clientSiret },
              { key: t("client.form.company"), value: data?.clientCompany },
              {
                key: t("driver.driving-licence-number"),
                value: data?.clientDrivingLicenceNumber,
              },
            ]}
            title={t("delivery.summary.client-information")}
          />
        </Grid>
        <Grid size={{ xl: 6, xs: 12 }}>
          <DataComponent
            data={[
              {
                key: t("delivery.summary.pick-up-date"),
                value: formatTimeDate(data.carPickUpDate ?? ""),
              },
              {
                key: t("delivery.summary.availability-date"),
                value: formatTimeDate(data.carAvailabilityDate ?? ""),
              },
              {
                key: t("delivery.summary.arrival-date"),
                value: formatTimeDate(data.carDeliveryDeadline ?? ""),
              },
            ]}
            title={t("delivery.summary.availability")}
          />
        </Grid>
        <Grid size={{ xl: 6, xs: 12 }}>
          <DataComponent
            data={[
              { key: "VIN", value: vehicles[0]?.vin },
              {
                key: t("delivery.form.vehicle.licence-plate"),
                value: vehicles[0]?.vehicleCode,
              },
              {
                key: t("delivery.form.vehicle.brand"),
                value: vehicles[0]?.marque,
              },
              {
                key: t("delivery.form.vehicle.model"),
                value: vehicles[0]?.model,
              },
              {
                key: t("delivery.form.vehicle.motorization.title"),
                value: vehicles[0]?.type,
              },
              {
                key: t("delivery.form.vehicle.category.title"),
                value: t(
                  `delivery.form.vehicle.category.${(vehicles[0]?.carType || "").toLowerCase()}`
                ),
              },
            ]}
            title={t("delivery.summary.delivered-vehicle-information")}
          />
        </Grid>
        {isRestitution && (
          <Grid size={{ xl: 6, xs: 12 }}>
            <DataComponent
              data={[
                { key: "VIN", value: vehicles[1]?.vin },
                {
                  key: t("delivery.form.vehicle.licence-plate"),
                  value: vehicles[1]?.vehicleCode,
                },
                {
                  key: t("delivery.form.vehicle.brand"),
                  value: vehicles[1]?.marque,
                },
                {
                  key: t("delivery.form.vehicle.model"),
                  value: vehicles[1]?.model,
                },
                {
                  key: t("delivery.form.vehicle.motorization.title"),
                  value: vehicles[1]?.carType,
                },
              ]}
              title={t("delivery.summary.restituted-vehicle-information")}
            />
          </Grid>
        )}
        <Grid size={{ xl: 6, xs: 12 }}>
          <Paper sx={{ backgroundColor: "transparent", padding: 3, height: "100%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography color="#ff5c8d" textTransform="capitalize" variant="h5">
                {t("delivery.summary.additional-options")}
              </Typography>
              {editable && isAdmin && (
                <Tooltip title={t("delivery.summary.add-option", "Add option")}>
                  <IconButton size="small" onClick={() => setAddOptionOpen(true)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="column" marginTop={2} spacing={1}>
              <AdditionalOptionsDisplay
                options={selectedOptions}
                deliveryId={data.id!}
                editable={editable}
              />
            </Stack>
            {editable && (
              <AddOptionModal
                key={String(addOptionOpen)}
                deliveryId={data.id}
                organizationSlug={organizationSlug}
                alreadyLinkedIds={alreadyLinkedIds}
                open={addOptionOpen}
                onClose={() => setAddOptionOpen(false)}
              />
            )}
          </Paper>
        </Grid>
        {isAdmin && data.selectedDriver && (
          <Grid size={{ xl: 6, xs: 12 }}>
            <DataComponent
              data={[
                {
                  key: t("delivery.summary.name"),
                  value: `${data?.selectedDriver?.firstName} ${data?.selectedDriver?.lastName}`,
                },
                {
                  key: t("common.form.input.phone"),
                  value: data?.selectedDriver?.phoneNumber,
                },
                {
                  key: t("common.form.input.email"),
                  value: data?.selectedDriver?.email,
                },
                ...(isAdmin
                  ? [
                      {
                        key: " ",
                        value: (
                          <Button
                            fullWidth
                            onClick={async () =>
                              navigate(
                                `/admin/drivers/drivers-table/accepted-requests/${data?.selectedDriver?.id}`
                              )
                            }
                            style={{ height: "fit-content" }}
                          >
                            {t("delivery.summary.show-more")}
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
              title={t("delivery.summary.driver-information")}
            />
          </Grid>
        )}
        <Grid size={{ xl: 6, xs: 12 }}>
          <DataComponent
            data={[
              {
                key: " ",
                value:
                  typeof data?.category === "string"
                    ? (categories[data.category] ?? data.category)
                    : null,
              },
            ]}
            title={t("delivery.summary.mission-category")}
          />
        </Grid>
        <Grid size={{ xl: 6, xs: 12 }}>
          <DataComponent
            data={[
              {
                key: " ",
                value: data?.comment,
              },
            ]}
            title={t("delivery.form.comment")}
          />
        </Grid>
      </Grid>
    </Container>
  );
}
